/**
 * PTE Renderers — 22 scored task types + Personal Introduction.
 *
 * A single file exports one component per task-type key. The Practice Player
 * dispatches on `question.question_type`. Each renderer:
 *   • is a pure component receiving { question, onResponseChange, submitted, result }
 *   • calls onResponseChange(response) on any change so the player can autosave
 *   • honours submitted flag to disable inputs after submission
 *   • exposes data-testids for QA
 *
 * Speaking renderers use SpeakingRecorder (a shared component that handles
 * mic permissions, MediaRecorder lifecycle, prep→record state transitions,
 * error recovery, upload).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pteApi } from '@/lib/pteApi';
import { Mic, MicOff, StopCircle, Loader2, Play, Volume2, CheckCircle2, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
const Countdown = ({ seconds, running, onFinish, label }) => {
  const [t, setT] = useState(seconds);
  useEffect(() => setT(seconds), [seconds]);
  useEffect(() => {
    if (!running) return;
    if (t <= 0) { onFinish?.(); return; }
    const h = setTimeout(() => setT((x) => x - 1), 1000);
    return () => clearTimeout(h);
  }, [t, running, onFinish]);
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-mono tracking-wider" data-testid="pte-timer">
      {label && <span className="text-slate-300 uppercase text-[10px]">{label}</span>}
      <span>{Math.max(0, t)}s</span>
    </div>
  );
};

const CardShell = ({ children, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testid}>
    {children}
  </div>
);

const Prompt = ({ children }) => (
  <div className="text-slate-800 leading-relaxed text-base md:text-lg" data-testid="pte-prompt">
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Speaking recorder
// ---------------------------------------------------------------------------
const SpeakingRecorder = ({ question, prepSeconds = 0, responseSeconds = 40, onUploaded, disabled }) => {
  const [phase, setPhase] = useState('idle');       // idle | mic_denied | ready | preparing | recording | uploading | done | error
  const [error, setError] = useState(null);
  const [uploadUrl, setUploadUrl] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);

  const requestMic = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase('mic_denied');
      setError('This device doesn’t support microphone recording. Please try on a desktop or laptop.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try {
          setPhase('uploading');
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const r = await pteApi.uploadSpeaking(blob, question.id || question.question_id);
          setUploadUrl(r.recording_url);
          onUploaded?.(r.recording_url);
          setPhase('done');
        } catch (e) {
          setPhase('error');
          setError('We couldn’t save that recording. Please try again — your progress is safe.');
        }
      };
      mediaRef.current = { stream, rec };
      setPhase(prepSeconds > 0 ? 'preparing' : 'ready');
    } catch (e) {
      setPhase('mic_denied');
      setError('We need permission to use your microphone. Please allow access in your browser and retry.');
    }
  }, [question, prepSeconds, onUploaded]);

  const startRecording = useCallback(() => {
    if (!mediaRef.current) return;
    mediaRef.current.rec.start();
    setPhase('recording');
    stopTimerRef.current = setTimeout(() => {
      try { mediaRef.current.rec.state === 'recording' && mediaRef.current.rec.stop(); } catch {}
    }, responseSeconds * 1000);
  }, [responseSeconds]);

  const stopEarly = useCallback(() => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    try { mediaRef.current?.rec?.state === 'recording' && mediaRef.current.rec.stop(); } catch {}
  }, []);

  const retry = useCallback(() => {
    setPhase('idle'); setError(null); setUploadUrl(null);
    chunksRef.current = [];
  }, []);

  useEffect(() => () => {
    stopTimerRef.current && clearTimeout(stopTimerRef.current);
    try { mediaRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); } catch {}
  }, []);

  return (
    <div className="rounded-xl bg-slate-900 text-white p-5" data-testid="speaking-recorder">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {phase === 'recording' ? (
            <span className="inline-flex items-center gap-2 text-red-400"><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />RECORDING</span>
          ) : phase === 'preparing' ? (
            <span className="inline-flex items-center gap-2 text-amber-300"><Mic className="h-4 w-4" />PREPARING</span>
          ) : phase === 'ready' ? (
            <span className="inline-flex items-center gap-2 text-emerald-300"><Mic className="h-4 w-4" />READY</span>
          ) : phase === 'uploading' ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />SAVING</span>
          ) : phase === 'done' ? (
            <span className="inline-flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" />RESPONSE SAVED</span>
          ) : phase === 'mic_denied' || phase === 'error' ? (
            <span className="inline-flex items-center gap-2 text-amber-300"><AlertCircle className="h-4 w-4" />{error}</span>
          ) : (
            <span className="inline-flex items-center gap-2 text-slate-300"><MicOff className="h-4 w-4" />GET READY</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {phase === 'preparing' && (
            <Countdown seconds={prepSeconds} running onFinish={startRecording} label="prep" />
          )}
          {phase === 'recording' && (
            <Countdown seconds={responseSeconds} running onFinish={stopEarly} label="rec" />
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {phase === 'idle' && (
          <button onClick={requestMic} disabled={disabled}
            data-testid="mic-enable"
            className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black text-sm font-bold">
            Enable microphone
          </button>
        )}
        {phase === 'ready' && (
          <button onClick={startRecording} disabled={disabled}
            data-testid="mic-record"
            className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold inline-flex items-center gap-1.5">
            <Mic className="h-4 w-4" /> Start recording
          </button>
        )}
        {phase === 'recording' && (
          <button onClick={stopEarly}
            data-testid="mic-stop"
            className="px-4 py-2 rounded-full bg-slate-700 hover:bg-slate-800 text-white text-sm inline-flex items-center gap-1.5">
            <StopCircle className="h-4 w-4" /> Stop
          </button>
        )}
        {(phase === 'error' || phase === 'mic_denied') && (
          <button onClick={retry}
            data-testid="mic-retry"
            className="px-4 py-2 rounded-full bg-white text-slate-900 text-sm">Retry</button>
        )}
        {phase === 'done' && uploadUrl && (
          <audio controls src={(process.env.REACT_APP_BACKEND_URL || '') + uploadUrl}
            className="max-w-full" data-testid="mic-playback" />
        )}
      </div>
    </div>
  );
};

// Simple audio-stimulus player. Uses TTS-friendly browser speech synthesis as a
// preview stand-in (Phase 1A) since Winhouse-original audio files are Phase 1B.
const AudioStimulus = ({ transcript, audioUrl, audioSource, autoPlayOnMount = true }) => {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const isPreview = audioSource === 'gtts_preview';

  const play = useCallback(async () => {
    setError(null);
    // Prefer real MP3 if available
    if (audioUrl && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        setPlaying(true);
      } catch (_) {
        setError('Audio could not be loaded. Please check your connection and try again.');
      }
      return;
    }
    // Legacy TTS fallback
    if (!window.speechSynthesis) {
      setError('Audio could not be loaded. Please check your connection and try again.');
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(transcript || '');
    u.rate = 0.95; u.pitch = 1.0; u.onend = () => setPlaying(false);
    setPlaying(true);
    window.speechSynthesis.speak(u);
  }, [audioUrl, transcript]);

  useEffect(() => {
    if (autoPlayOnMount) play();
    return () => { window.speechSynthesis?.cancel(); };
  }, [autoPlayOnMount, play]);

  return (
    <div className="rounded-xl bg-slate-100 p-4 border border-slate-200 flex items-center gap-3" data-testid="audio-stimulus">
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="auto" onEnded={() => setPlaying(false)}
          onError={() => setError('Audio could not be loaded. Please check your connection and try again.')} />
      )}
      <button onClick={play} disabled={playing}
        className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center disabled:opacity-60"
        data-testid="audio-play">
        {playing ? <Volume2 className="h-4 w-4 animate-pulse" /> : <Play className="h-4 w-4" />}
      </button>
      <div className="text-sm text-slate-600 flex-1">
        {error ? (
          <span className="text-amber-700" data-testid="audio-error">{error} <button onClick={play} className="underline">Retry</button></span>
        ) : playing ? 'Audio playing…' : 'Press play to hear the audio stimulus.'}
        {isPreview && !error && (
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Preview audio · professional Winhouse audio pending</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Speaking renderers
// ---------------------------------------------------------------------------
export const ReadAloud = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-read-aloud">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Read Aloud</div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={40} responseSeconds={40}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })}
        disabled={disabled} />
    </div>
  </CardShell>
);

export const RepeatSentence = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-repeat-sentence">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Repeat Sentence</div>
    <div className="text-slate-700 mb-3">Listen to the sentence. Then repeat it.</div>
    <AudioStimulus transcript={question.transcript || question.tts_text || question.prompt}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={0} responseSeconds={15}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
    </div>
  </CardShell>
);

export const DescribeImage = ({ question, onResponseChange, disabled }) => {
  const [zoom, setZoom] = useState(false);
  return (
    <CardShell testid="rr-describe-image">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Describe Image</div>
        {question.image_url && (
          <button onClick={() => setZoom(true)} data-testid="di-zoom"
            className="text-[11px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700">
            Enlarge
          </button>
        )}
      </div>
      {question.image_url ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 flex justify-center">
          <img src={question.image_url} alt={question.image_description || 'Describe Image stimulus'}
            className="w-full max-w-2xl" data-testid="di-image" />
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-600 mb-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Stimulus preview</div>
          <div className="italic">{question.image_description || question.prompt}</div>
        </div>
      )}
      <SpeakingRecorder question={question} prepSeconds={25} responseSeconds={40}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoom(false)} data-testid="di-zoom-modal">
          <img src={question.image_url} alt="" className="max-w-full max-h-full" />
        </div>
      )}
    </CardShell>
  );
};

export const RetellLecture = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-retell-lecture">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Retell Lecture</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={10} responseSeconds={40}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
    </div>
  </CardShell>
);

export const AnswerShortQuestion = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-answer-short-question">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Answer Short Question</div>
    <AudioStimulus transcript={question.transcript || question.tts_text || question.prompt}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={0} responseSeconds={10}
        onUploaded={(u) => onResponseChange?.({ recording_url: u, response: '' })} disabled={disabled} />
    </div>
  </CardShell>
);

export const SummarizeGroupDiscussion = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-summarize-group-discussion">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Summarize Group Discussion</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={20} responseSeconds={60}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
    </div>
  </CardShell>
);

export const RespondToSituation = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-respond-to-situation">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Respond to a Situation</div>
    <AudioStimulus transcript={question.transcript || question.tts_text || question.prompt}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={20} responseSeconds={40}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
    </div>
  </CardShell>
);

export const PersonalIntroduction = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-personal-intro">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Personal Introduction · Non-scored</div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4">
      <SpeakingRecorder question={question} prepSeconds={25} responseSeconds={30}
        onUploaded={(u) => onResponseChange?.({ recording_url: u })} disabled={disabled} />
    </div>
  </CardShell>
);

// ---------------------------------------------------------------------------
// Writing renderers
// ---------------------------------------------------------------------------
const WordCounter = ({ text, min, max }) => {
  const count = useMemo(() => (text || '').trim().split(/\s+/).filter(Boolean).length, [text]);
  const ok = count >= min && count <= max;
  return (
    <div className={`text-xs font-mono ${ok ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="word-count">
      Words: <strong>{count}</strong> · Target {min}–{max}
    </div>
  );
};

const WritingPad = ({ question, onResponseChange, wordMin, wordMax, timerSeconds, disabled }) => {
  const [text, setText] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => { onResponseChange?.({ response: text }); setSavedAt(new Date()); }, 800);
    return () => clearTimeout(t);
  }, [text, onResponseChange]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {timerSeconds && <Countdown seconds={timerSeconds} running={!disabled} label="time" />}
        <WordCounter text={text} min={wordMin} max={wordMax} />
        <div className="text-xs text-slate-500" data-testid="autosave-status">
          {savedAt ? `Saved · ${savedAt.toLocaleTimeString()}` : 'Not saved'}
        </div>
      </div>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} disabled={disabled}
        placeholder="Type your response here…"
        className="w-full min-h-[220px] rounded-xl border border-slate-300 focus:border-slate-900 outline-none p-4 text-base leading-relaxed"
        data-testid="writing-pad"
      />
    </div>
  );
};

export const SummarizeWrittenText = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-summarize-written-text">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Summarize Written Text</div>
    <div className="rounded-lg bg-slate-50 p-4 mb-3 text-slate-800 leading-relaxed" data-testid="passage">
      {question.passage}
    </div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4">
      <WritingPad question={question} onResponseChange={onResponseChange}
        wordMin={5} wordMax={75} timerSeconds={10 * 60} disabled={disabled} />
    </div>
  </CardShell>
);

export const WriteEssay = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-write-essay">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Write Essay</div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4">
      <WritingPad question={question} onResponseChange={onResponseChange}
        wordMin={200} wordMax={300} timerSeconds={20 * 60} disabled={disabled} />
    </div>
  </CardShell>
);

export const SummarizeSpokenText = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-summarize-spoken-text">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Summarize Spoken Text</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4">
      <WritingPad question={question} onResponseChange={onResponseChange}
        wordMin={50} wordMax={70} timerSeconds={10 * 60} disabled={disabled} />
    </div>
  </CardShell>
);

// ---------------------------------------------------------------------------
// Reading & Listening interactive renderers
// ---------------------------------------------------------------------------
export const RWFibDropdown = ({ question, onResponseChange, disabled }) => {
  const blanks = question.blanks || {};
  const [values, setValues] = useState({});
  const setVal = (k, v) => { const next = { ...values, [k]: v }; setValues(next); onResponseChange?.({ response: next }); };
  const passage = (question.prompt || '').split(/(___BLANK\d+___)/g);
  return (
    <CardShell testid="rr-rw-fib-dropdown">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">R&W: Fill in the Blanks</div>
      <div className="text-slate-800 leading-loose" data-testid="passage">
        {passage.map((seg, i) => {
          const m = seg.match(/___BLANK(\d+)___/);
          if (!m) return <span key={i}>{seg}</span>;
          const key = `BLANK${m[1]}`;
          const opts = blanks[key]?.options || [];
          return (
            <select key={i} value={values[key] || ''}
              onChange={(e) => setVal(key, e.target.value)} disabled={disabled}
              data-testid={`fib-select-${key}`}
              className="mx-1 px-2 py-1 rounded-md border-2 border-slate-300 focus:border-slate-900 outline-none bg-white text-sm">
              <option value="">— choose —</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        })}
      </div>
    </CardShell>
  );
};

const MCList = ({ question, multi, onResponseChange, disabled, testidPrefix }) => {
  const [picked, setPicked] = useState(multi ? new Set() : null);
  const toggle = (key) => {
    if (multi) {
      const next = new Set(picked); next.has(key) ? next.delete(key) : next.add(key);
      setPicked(next); onResponseChange?.({ response: [...next] });
    } else {
      setPicked(key); onResponseChange?.({ response: key });
    }
  };
  return (
    <div className="space-y-2">
      {(question.options || []).map((o) => {
        const isPicked = multi ? picked.has(o.key) : picked === o.key;
        return (
          <button key={o.key} onClick={() => toggle(o.key)} disabled={disabled}
            data-testid={`${testidPrefix}-${o.key}`}
            className={`w-full text-left p-3 rounded-lg border transition ${isPicked ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 hover:border-slate-400 bg-white'}`}>
            <span className="font-mono text-xs mr-2 opacity-70">{o.key}</span>{o.text}
          </button>
        );
      })}
    </div>
  );
};

export const ReadingMCMA = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-reading-mcma">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Reading · MC Multiple Answers</div>
    <div className="rounded-lg bg-slate-50 p-4 mb-3 text-slate-800 leading-relaxed" data-testid="passage">{question.passage}</div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4"><MCList question={question} multi onResponseChange={onResponseChange} disabled={disabled} testidPrefix="mcma-r" /></div>
  </CardShell>
);

export const ReadingMCSA = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-reading-mcsa">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Reading · MC Single Answer</div>
    <div className="rounded-lg bg-slate-50 p-4 mb-3 text-slate-800 leading-relaxed" data-testid="passage">{question.passage}</div>
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4"><MCList question={question} multi={false} onResponseChange={onResponseChange} disabled={disabled} testidPrefix="mcsa-r" /></div>
  </CardShell>
);

export const ListeningMCMA = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-listening-mcma">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Listening · MC Multiple Answers</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4"><MCList question={question} multi onResponseChange={onResponseChange} disabled={disabled} testidPrefix="mcma-l" /></div>
  </CardShell>
);

export const ListeningMCSA = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-listening-mcsa">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Listening · MC Single Answer</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4"><MCList question={question} multi={false} onResponseChange={onResponseChange} disabled={disabled} testidPrefix="mcsa-l" /></div>
  </CardShell>
);

export const HighlightCorrectSummary = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-highlight-correct-summary">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Highlight Correct Summary</div>
    <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <div className="mt-4"><MCList question={question} multi={false} onResponseChange={onResponseChange} disabled={disabled} testidPrefix="hcs" /></div>
  </CardShell>
);

export const SelectMissingWord = ({ question, onResponseChange, disabled }) => (
  <CardShell testid="rr-select-missing-word">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Select Missing Word</div>
    <AudioStimulus transcript={((question.transcript || question.tts_text) || question.prompt).replace('[beep]', '(beep)')}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
    <Prompt>{question.prompt}</Prompt>
    <div className="mt-4"><MCList question={question} multi={false} onResponseChange={onResponseChange} disabled={disabled} testidPrefix="smw" /></div>
  </CardShell>
);

// Reorder Paragraph
export const ReorderParagraph = ({ question, onResponseChange, disabled }) => {
  const [order, setOrder] = useState(() => (question.sentences || []).map((s) => s.key));
  useEffect(() => onResponseChange?.({ response: order }), [order, onResponseChange]);
  const move = (idx, dir) => {
    if (disabled) return;
    const j = idx + dir; if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[idx], next[j]] = [next[j], next[idx]]; setOrder(next);
  };
  const byKey = Object.fromEntries((question.sentences || []).map((s) => [s.key, s.text]));
  return (
    <CardShell testid="rr-reorder-paragraph">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Reorder Paragraph</div>
      <Prompt>{question.prompt}</Prompt>
      <ol className="mt-4 space-y-2" data-testid="reorder-list">
        {order.map((k, i) => (
          <li key={k} className="p-3 rounded-lg border border-slate-200 bg-white flex items-start gap-3" data-testid={`reorder-item-${k}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={disabled || i === 0}
                data-testid={`reorder-up-${k}`}
                className="text-slate-400 hover:text-slate-900 disabled:opacity-30">▲</button>
              <button onClick={() => move(i, 1)} disabled={disabled || i === order.length - 1}
                data-testid={`reorder-down-${k}`}
                className="text-slate-400 hover:text-slate-900 disabled:opacity-30">▼</button>
            </div>
            <div className="text-slate-800 flex-1">{byKey[k]}</div>
          </li>
        ))}
      </ol>
    </CardShell>
  );
};

// Reading FIB Drag — click word bank to fill next empty blank
export const ReadingFibDrag = ({ question, onResponseChange, disabled }) => {
  const bank = question.word_bank || [];
  const parts = (question.prompt || '').split(/(___B\d+___)/g);
  const numBlanks = parts.filter((p) => /___B\d+___/.test(p)).length;
  const [filled, setFilled] = useState(Array(numBlanks).fill(''));
  const [available, setAvailable] = useState(bank);
  useEffect(() => onResponseChange?.({ response: filled }), [filled, onResponseChange]);
  const pickWord = (w) => {
    if (disabled) return;
    const emptyIdx = filled.findIndex((x) => !x); if (emptyIdx < 0) return;
    const next = [...filled]; next[emptyIdx] = w; setFilled(next);
    setAvailable(available.filter((x) => x !== w));
  };
  const clearBlank = (i) => {
    if (disabled) return;
    const w = filled[i]; if (!w) return;
    const next = [...filled]; next[i] = ''; setFilled(next);
    setAvailable([...available, w]);
  };
  let blankIdx = 0;
  return (
    <CardShell testid="rr-reading-fib-drag">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Reading · Fill in the Blanks</div>
      <div className="text-slate-800 leading-loose" data-testid="passage">
        {parts.map((seg, i) => {
          if (/___B\d+___/.test(seg)) {
            const idx = blankIdx++;
            return (
              <button key={i} onClick={() => clearBlank(idx)} disabled={disabled}
                data-testid={`fib-drag-blank-${idx}`}
                className={`inline-block mx-1 min-w-[80px] px-2 py-0.5 rounded border-b-2 ${filled[idx] ? 'border-slate-900 bg-slate-100 text-slate-900' : 'border-slate-400 bg-slate-50 text-slate-400'}`}>
                {filled[idx] || '____'}
              </button>
            );
          }
          return <span key={i}>{seg}</span>;
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="word-bank">
        {available.map((w) => (
          <button key={w} onClick={() => pickWord(w)} disabled={disabled}
            data-testid={`word-bank-${w}`}
            className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-700">{w}</button>
        ))}
      </div>
    </CardShell>
  );
};

// Listening FIB — same idea but with audio + typed blanks
export const ListeningFib = ({ question, onResponseChange, disabled }) => {
  const parts = (question.prompt || '').split(/(___B\d+___)/g);
  const numBlanks = parts.filter((p) => /___B\d+___/.test(p)).length;
  const [values, setValues] = useState(Array(numBlanks).fill(''));
  useEffect(() => onResponseChange?.({ response: values }), [values, onResponseChange]);
  let blankIdx = 0;
  return (
    <CardShell testid="rr-listening-fib">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Listening · Fill in the Blanks</div>
      <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
      <div className="mt-4 text-slate-800 leading-loose" data-testid="transcript">
        {parts.map((seg, i) => {
          if (/___B\d+___/.test(seg)) {
            const idx = blankIdx++;
            return (
              <input key={i} type="text" value={values[idx]} disabled={disabled}
                onChange={(e) => { const next = [...values]; next[idx] = e.target.value; setValues(next); }}
                data-testid={`listen-fib-input-${idx}`}
                className="mx-1 px-2 py-0.5 rounded border-b-2 border-slate-300 focus:border-slate-900 bg-slate-50 outline-none w-32" />
            );
          }
          return <span key={i}>{seg}</span>;
        })}
      </div>
    </CardShell>
  );
};

// Highlight Incorrect Words — click on words in printed transcript
export const HighlightIncorrectWords = ({ question, onResponseChange, disabled }) => {
  const words = useMemo(() => (question.printed_transcript || question.printed_transcript_display || '').split(/(\s+)/), [question]);
  const [selected, setSelected] = useState(new Set());
  const toggle = (w) => {
    if (disabled) return;
    const key = w.toLowerCase().replace(/[^a-z']/g, '');
    if (!key) return;
    const next = new Set(selected); next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next); onResponseChange?.({ response: [...next] });
  };
  return (
    <CardShell testid="rr-highlight-incorrect-words">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Highlight Incorrect Words</div>
      <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
      <div className="mt-4 text-slate-800 leading-loose" data-testid="highlight-transcript">
        {words.map((w, i) => {
          if (/^\s+$/.test(w)) return <span key={i}>{w}</span>;
          const key = w.toLowerCase().replace(/[^a-z']/g, '');
          const active = selected.has(key);
          return (
            <button key={i} onClick={() => toggle(w)} disabled={disabled}
              data-testid={`hiw-word-${i}`}
              className={`inline mx-0.5 px-0.5 rounded ${active ? 'bg-amber-300 font-semibold' : 'hover:bg-slate-100'}`}>{w}</button>
          );
        })}
      </div>
    </CardShell>
  );
};

// Write From Dictation — audio + single text field
export const WriteFromDictation = ({ question, onResponseChange, disabled }) => {
  const [text, setText] = useState('');
  useEffect(() => onResponseChange?.({ response: text }), [text, onResponseChange]);
  return (
    <CardShell testid="rr-write-from-dictation">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Write From Dictation</div>
      <AudioStimulus transcript={question.transcript || question.tts_text}
      audioUrl={question.audio_url} audioSource={question.audio_source} />
      <div className="mt-4">
        <input type="text" value={text} onChange={(e) => setText(e.target.value)} disabled={disabled}
          data-testid="dictation-input"
          placeholder="Type the sentence you hear…"
          className="w-full p-3 rounded-xl border border-slate-300 focus:border-slate-900 outline-none text-base" />
      </div>
    </CardShell>
  );
};

// ---------------------------------------------------------------------------
// Renderer dispatcher — Practice Player looks up by question_type
// ---------------------------------------------------------------------------
export const RENDERERS = {
  pte_read_aloud: ReadAloud,
  pte_repeat_sentence: RepeatSentence,
  pte_describe_image: DescribeImage,
  pte_retell_lecture: RetellLecture,
  pte_answer_short_question: AnswerShortQuestion,
  pte_summarize_group_discussion: SummarizeGroupDiscussion,
  pte_respond_to_situation: RespondToSituation,
  pte_personal_introduction: PersonalIntroduction,
  pte_summarize_written_text: SummarizeWrittenText,
  pte_write_essay: WriteEssay,
  pte_summarize_spoken_text: SummarizeSpokenText,
  pte_rw_fib_dropdown: RWFibDropdown,
  pte_reading_mcma: ReadingMCMA,
  pte_reading_mcsa: ReadingMCSA,
  pte_listening_mcma: ListeningMCMA,
  pte_listening_mcsa: ListeningMCSA,
  pte_highlight_correct_summary: HighlightCorrectSummary,
  pte_select_missing_word: SelectMissingWord,
  pte_reorder_paragraph: ReorderParagraph,
  pte_reading_fib_drag: ReadingFibDrag,
  pte_listening_fib: ListeningFib,
  pte_highlight_incorrect_words: HighlightIncorrectWords,
  pte_write_from_dictation: WriteFromDictation,
};

export default RENDERERS;
